import { getTokenHeader } from "@/helpers/commonHelper";
import axios from "axios";
import moment from "moment";
import { flatten } from "lodash";
const env = require(`@/environments/${process.env.VUE_APP_ENV_PATH}`);
import store from "@/store";
import { removeXMLInvalidChars } from "@/helpers/newsGbXmlHelper";
import { headerEntities } from "@/helpers/headerEntities";
import { DateTime } from "luxon";
const features =
  flatten(store.state.featureGroupsForEvents?.map(fg => fg.features)) ?? [];

function getOrganizer(item) {
  const { operator } = item ?? {};
  if (operator && store.state.organizerById?.[operator])
    return store.state.organizerById[operator];
}

function getCategoryGroup(item) {
  const { category_group } = item ?? {};
  if (category_group) {
    const allCategoryGroups = store.state.categoryGroupsForEvents;
    const foundCategoryGroup = allCategoryGroups.find(
      cat => cat._id === category_group
    );
    if (foundCategoryGroup && foundCategoryGroup?.title)
      return foundCategoryGroup.title;
  }
}

function getCategory(item) {
  const { category } = item ?? {};
  if (category) {
    const allCategories = store.state.categoryGroupsForEvents.flatMap(
      categoryGroup => categoryGroup.categories
    );
    const foundCategory = allCategories.find(cat => cat._id === category);
    if (foundCategory && foundCategory?.title) return foundCategory.title;
  }
}

function getLocation(event) {
  return store.state.locations.find(loc => loc._id === event.location);
}
export function dateFormat(date, allDay = "") {
  let dateTime = DateTime.fromISO(date).isValid
    ? DateTime.fromISO(date)
    : DateTime.fromJSDate(date);
  if (!allDay)
    //f.e.: 01.12.2023 11:21
    return dateTime.toFormat("dd.LL.yyyy T");
  return dateTime.toFormat("dd.LL.yyyy");
}

export function formatToTime(dateObj, prefix = "") {
  const fromTime = DateTime.fromISO(dateObj.from).toFormat("HH:mm");
  const toTime = DateTime.fromISO(dateObj.to).toFormat("HH:mm");
  if (fromTime === toTime) {
    return "";
  }
  let output;
  //if event-duration is more than 24h - show date with time instead
  if (new Date(dateObj.to) - new Date(dateObj.from) > 24 * 60 * 60 * 1000) {
    output = dateFormat(dateObj.to);
    return prefix ? prefix + " " + output : output;
  }
  output = toTime;
  return prefix ? prefix + " " + output : output;
}

export function stringifyUTF8(string = "") {
  string = parseReturns(string);
  const tempDivElement = document.createElement("div");
  tempDivElement.innerHTML = string;
  const text = tempDivElement.textContent || tempDivElement.innerText || "";
  return removeXMLInvalidChars(text);
}

function parseReturns(string = "") {
  return string
    ?.toString()
    ?.replace("<p>", "")
    ?.replace("<P>", "")
    ?.replace("</p>", "\n")
    ?.replace("</P>", "\n")
    ?.replace("<br>", "\n")
    ?.replace("<BR>", "\n")
    ?.replace("</br>", "\n")
    ?.replace("</BR>", "\n")
    ?.replace(/&nbsp;/gi, " ");
}

function calculateDurationFromMoment(moment) {
  const fromInMs = new Date(moment.from).getTime();
  const toInMs = new Date(moment.to).getTime();

  const durationInMs = toInMs - fromInMs;
  const durationInMinutes = durationInMs / 60000;
  return durationInMinutes;
}

export function getIsAllDay(event) {
  const isAllDay =
    event.moment.all_day || (event.all_day && event.moment.mainMoment);
  return isAllDay;
}

function getDuration(event) {
  if (getIsAllDay(event)) {
    return "";
  }
  const duration =
    event.moment?.duration ?? calculateDurationFromMoment(event.moment);
  if (!duration || isNaN(duration)) {
    return "";
  }

  let hours = Math.floor(duration / 60);
  if (hours < 10) hours = `0${hours}`;

  let minutes = Math.floor(duration - hours * 60);
  if (minutes < 10) minutes = `0${minutes}`;

  if (Number(hours) === 0 && Number(minutes) === 0) {
    return "";
  }
  return `${hours}:${minutes}`;
}

export async function loadAppContentTypes() {
  const headers = getTokenHeader();
  const params = {
    principal_id: this.selectedPrincipal._id
  };
  try {
    const response = await axios.get(
      env.events_api_client_url + "app_content_type/",
      {
        headers,
        params
      }
    );
    this.appContentTypes = response.data;
  } catch (error) {
    this.createError(error);
  }
}

export function getWeekDay(date) {
  switch (moment(date).day()) {
    case 0:
      return "Sonntag";
    case 1:
      return "Montag";
    case 2:
      return "Dienstag";
    case 3:
      return "Mittwoch";
    case 4:
      return "Donnerstag";
    case 5:
      return "Freitag";
    case 6:
      return "Samstag";
  }
}

export function getRegionality(regionality) {
  switch (regionality) {
    case "communal":
      return "Regional";
    case "local":
      return "Lokal";
    case "regional":
      return "Überregional";
    case "internal":
      return "Intern";
    default:
      return "";
  }
}

export function getUsersNameById(id, users = store.state.users) {
  const user = users?.find(u => u.id === id);
  return `${user?.first_name ?? ""} ${user?.last_name ?? ""}`;
}

export function getXMLArrayForEvent(event, selectedExportFields) {
  const pathStart = event?.pictures?.[0]?.url?.indexOf("/events/");
  // +8 Because of length of /events/
  const imagePath = event?.pictures?.[0]?.url?.slice(pathStart + 8) ?? "";

  const selectableChildren = flatten(
    SELECTABLE_EXPORT_FIELDS.map(field => field.children)
  );

  const exportFields = selectedExportFields.map(fieldId =>
    selectableChildren.find(field => field.id === fieldId)
  );

  return exportFields.map(field => {
    if (field?.id === "ID")
      return {
        name: field.title,
        text: field.getValue(event),
        attrs: { bild: imagePath }
      };
    else return { name: field.title, text: field.getValue(event) };
  });
}

export const XML_HEADER_ENTITIES = headerEntities;

export const SELECTABLE_EXPORT_FIELDS = [
  {
    title: "Veranstaltung",
    id: "Veranstaltung",
    children: [
      {
        title: "Wochentag",
        id: "Wochentag",
        getValue: event =>
          event.moment?.from
            ? getWeekDay(event.moment.from)
            : getWeekDay(event.start)
      },
      {
        title: "Start",
        id: "Start",
        getValue: event =>
          event.moment?.from
            ? moment(event.moment.from).format("DD.MM.YYYY")
            : event?.start
            ? moment(event.start).format("DD.MM.YYYY")
            : ""
      },
      {
        title: "Start_Uhr",
        id: "Start_Uhr",
        getValue: event => {
          if (getIsAllDay(event)) {
            return "";
          }
          if (event.moment?.from) {
            return moment(event.moment.from).format("HH:mm");
          }
          return moment(event.start).format("HH:mm");
        }
      },
      {
        title: "Start_Serie",
        id: "Start_Serie",
        getValue: event => {
          if (event.type !== "series") {
            return "";
          }
          const moments = event.moments?.length
            ? [...event.moments]
            : [event.moment];
          moments.sort((a, b) => new Date(a.from) - new Date(b.from));

          const m = moments[0];
          return moment(m.from).format("DD.MM.YYYY");
        }
      },
      {
        title: "Dauer",
        id: "Dauer",
        getValue: event => getDuration(event)
      },
      {
        title: "Ganztägig",
        id: "Ganztägig",
        getValue: event => {
          return getIsAllDay(event) ? "Ja" : "Nein";
        }
      },
      {
        title: "Abgesagt",
        id: "Abgesagt",
        getValue: event =>
          event.moment?.cancelled || event.cancelled ? "Ja" : "Nein"
      },
      {
        title: "Wochentag_bis",
        id: "Wochentag_bis",
        getValue: event =>
          event.moment?.to ? getWeekDay(event.moment.to) : getWeekDay(event.end)
      },
      {
        title: "Ende",
        id: "Ende",
        getValue: event =>
          event.moment?.to
            ? moment(event.moment.to).format("DD.MM.YYYY")
            : event?.end
            ? moment(event.end).format("DD.MM.YYYY")
            : ""
      },
      {
        title: "Ende_Uhr",
        id: "Ende_Uhr",
        getValue: event => {
          if (getIsAllDay(event)) {
            return "";
          }
          if (event.moment?.to) {
            return moment(event.moment.to).format("HH:mm");
          }
          return moment(event.end).format("HH:mm");
        }
      },
      {
        title: "Ende_Serie",
        id: "Ende_Serie",
        getValue: event => {
          if (event.type !== "series") {
            return "";
          }
          const moments = event.moments?.length
            ? [...event.moments]
            : [event.moment];
          moments.sort((a, b) => new Date(b.to) - new Date(a.to));

          const m = moments[0];
          return moment(m.to).format("DD.MM.YYYY");
        }
      },
      {
        title: "Einzeltermine",
        id: "Einzeltermine",
        getValue: event => {
          if (!event.moments?.length) {
            event.moments = [event.moment];
          }
          const getDate = d => (d ? moment(d).format("DD.MM.YYYY") : "");
          const getTime = d => (d ? moment(d).format("HH:mm") : "");
          return event.moments
            .map(moment => {
              if (moment.all_day) {
                return `${getDate(moment.from)} (Ganztägig)`;
              }
              const from = {
                d: getDate(moment.from),
                t: getTime(moment.from)
              };
              const to = {
                d: getDate(moment.to),
                t: getTime(moment.to)
              };
              if (from.t === to.t) {
                return `${from.d} (Ganztägig)`;
              }
              return `${from.d} (${from.t} — ${to.t} Uhr)`;
            })
            .join(", ");
        }
      },
      {
        title: "Titel",
        id: "Titel",
        getValue: event => event.moment?.title || event.title
      },
      {
        title: "Kurztitel",
        id: "Kurztitel",
        getValue: event => event.moment?.short_title || event.short_title
      },
      {
        title: "Beschreibung",
        id: "Beschreibung",
        getValue: event =>
          stringifyUTF8(event.moment?.description || event.description)
      },
      {
        title: "Aktiv (Veröffentlicht)",
        id: "Aktiv (Veröffentlicht)",
        getValue: event => (event.active ? "Ja" : "Nein")
      },
      {
        title: "Anreisebeschreibung",
        id: "Anreisebeschreibung",
        getValue: event =>
          (
            event.moment?.arrival_description ||
            event.arrival_description ||
            ""
          ).replace(/<[^>]*>/g, "")
      },
      {
        title: "Besitzer",
        id: "Besitzer",
        getValue: event => getUsersNameById(event.owner_id)
      },
      {
        title: "Erstellt_am",
        id: "Erstellt_am",
        getValue: event => event.create_date?.dateFormat()
      },
      {
        title: "External_Link",
        id: "External_Link",
        getValue: event => event.link_external ?? ""
      },
      {
        title: "Geändert_am",
        id: "Geändert_am",
        getValue: event => event.change_date?.dateFormat()
      },
      {
        title: "Geändert_durch",
        id: "Geändert_durch",
        getValue: event => getUsersNameById(event.updated_by)
      },
      {
        title: "Highlight",
        id: "Highlight",
        getValue: event => {
          const { useHighlightFrom } = store.state.eventConfig;
          return event.highlight[useHighlightFrom] ? "Ja" : "Nein";
        }
      },
      {
        title: "ID",
        id: "ID",
        getValue: event => event._id
      },
      {
        title: "Kategorie",
        id: "Kategorie",
        getValue: event => catHelper(event)
      },
      {
        title: "Kategoriegruppe",
        id: "Kategoriegruppe",
        getValue: event => catGroupHelper(event)
      },
      {
        title: "Kurzbeschreibung",
        id: "Kurzbeschreibung",
        getValue: event =>
          stringifyUTF8(
            event.moment?.short_description || event.short_description || ""
          )
      },
      {
        title: "Merkmale",
        id: "Merkmale",
        getValue: event =>
          (
            features
              .filter(feature => event.features?.includes(feature._id))
              ?.map(feature => feature.title) || ["---"]
          ).join(", ")
      },
      {
        title: "Preis_ab",
        id: "Preis_ab",
        getValue: event => event.price_from ?? ""
      },
      {
        title: "Preisbeschreibung",
        id: "Preisbeschreibung",
        getValue: event =>
          (
            event.moment?.price_description ||
            event.price_description ||
            ""
          ).replace(/<[^>]*>/g, "")
      },
      {
        title: "Regionalität",
        id: "Regionalität",
        getValue: event => getRegionality(event.regionality)
      },
      {
        title: "Typ",
        id: "Typ",
        getValue: event =>
          event.type === "basic" ? "Einzeltermin" : "Serientermin / Zeitraum"
      },
      {
        title: "Veranstaltungsort_Zusatzinformationen",
        id: "Veranstaltungsort_Zusatzinformationen",
        getValue: event => event.location_text ?? ""
      },
      {
        id: "Interner_Kommentar",
        title: "Interner_Kommentar",
        getValue: event => (event.comment?.length ? event.comment : "")
      },
      {
        id: "Interne_Anweisungen",
        title: "Interne_Anweisungen",
        getValue: event =>
          event.instructions?.length ? event.instructions : ""
      },
      {
        title: "Veröffentlicht",
        id: "Veröffentlicht",
        getValue: event => (event.active ? "Ja" : "Nein")
      }
    ]
  },
  {
    title: "Web-Felder_Veranstaltung",
    id: "Web_Veranstaltung",
    children: [
      {
        title: "Web_Beschreibung",
        id: "Web_Beschreibung",
        getValue: event =>
          stringifyUTF8(
            event.moment?.alternative_content?.web?.description ||
              event.alternative_content?.web?.description
          )
      },
      {
        title: "Web_Kurzbeschreibung",
        id: "Web_Kurzbeschreibung",
        getValue: event =>
          stringifyUTF8(
            event.moment?.alternative_content?.web?.short_description ||
              event.alternative_content?.web?.short_description
          )
      }
    ]
  },
  {
    title: "Print-Felder_Veranstaltung",
    id: "Print_Veranstaltung",
    children: [
      {
        title: "Print_Beschreibung",
        id: "Print_Beschreibung",
        getValue: event =>
          stringifyUTF8(
            event.moment?.alternative_content?.print?.description ||
              event.alternative_content?.print?.description
          )
      },
      {
        title: "Print_Kurzbeschreibung",
        id: "Print_Kurzbeschreibung",
        getValue: event =>
          stringifyUTF8(
            event.moment?.alternative_content?.print?.short_description ||
              event.alternative_content?.print?.short_description
          )
      }
    ]
  },
  {
    title: "Print2_Felder_Veranstaltung",
    id: "Print2_Veranstaltung",
    children: [
      {
        title: "Print2_Beschreibung",
        id: "Print2_Beschreibung",
        getValue: event =>
          stringifyUTF8(
            event.moment?.alternative_content?.print2?.description ||
              event.alternative_content?.print2?.description
          )
      },
      {
        title: "Print2_Kurzbeschreibung",
        id: "Print2_Kurzbeschreibung",
        getValue: event =>
          stringifyUTF8(
            event.moment?.alternative_content?.print2?.short_description ||
              event.alternative_content?.print2?.short_description
          )
      }
    ]
  },
  {
    title: "Standort",
    id: "Standort",
    children: [
      {
        title: "Standort_Titel",
        id: "Standort_Titel",
        getValue: event => getLocation(event)?.title ?? ""
      },

      {
        title: "Standort_Adresse",
        id: "Standort_Adresse",
        getValue: event => getLocation(event)?.address?.address ?? ""
      },
      {
        title: "Standort_E-Mail",
        id: "Standort_E-Mail",
        getValue: event => getLocation(event)?.address?.email ?? ""
      },
      {
        title: "Standort_ID",
        id: "Standort_ID",
        getValue: event => getLocation(event)?._id ?? ""
      },
      {
        title: "Standort_Internetadresse",
        id: "Standort_Internetadresse",
        getValue: event => getLocation(event)?.address?.website ?? ""
      },
      {
        title: "Standort_Ort",
        id: "Standort_Ort",
        getValue: event => getLocation(event)?.address?.city ?? ""
      },
      {
        title: "Standort_Uebergeordnet",
        id: "Standort_Uebergeordnet",
        getValue: event => getLocation(event)?.address?.normalized_city ?? ""
      },
      {
        title: "Standort_PLZ",
        id: "Standort_PLZ",
        getValue: event => getLocation(event)?.address?.zip ?? ""
      },
      {
        title: "Standort_Telefon",
        id: "Standort_Telefon",
        getValue: event => getLocation(event)?.address?.phone ?? ""
      }
    ]
  },
  {
    title: "Veranstalter",
    id: "operator",
    children: [
      {
        title: "Veranstalter",
        id: "Veranstalter",
        getValue: getOrganizer
      },
      {
        title: "Veranstalter_E-Mail",
        id: "Veranstalter_E-Mail",
        getValue: event => getOrganizer(event)?.address?.email ?? ""
      },
      {
        title: "Veranstalter_Freitext",
        id: "Veranstalter_Freitext",
        getValue: event => event.operator_text ?? ""
      },
      {
        title: "Veranstalter_Internetadresse",
        id: "Veranstalter_Internetadresse",
        getValue: event => getOrganizer(event)?.address?.website ?? ""
      },
      {
        title: "Veranstalter_Telefon",
        id: "Veranstalter_Telefon",
        getValue: event => getOrganizer(event)?.address?.phone ?? ""
      }
    ]
  }
];

/// Sort Options für Export.
/// Struktur SELECTABLE_EXPORT_FIELDS nachempfunden;
/// 'sortBy' muss inhaltlich der jeweiligen Funktion 'getValue' entsprechen.
/// Hier erneut implementiert, da Datentypen (z.B. Date) für Sortierung relevant.
export const SELECTABLE_EXPORT_SORT_OPTIONS = [
  {
    title: "Datum und Uhrzeit",
    id: "Start",
    default: true,
    sortBy: function(event) {
      if (event.moment?.from) return new Date(event.moment?.from);
      if (event.start) return new Date(event.start);
      return "";
    }
  },
  {
    title: "Datum",
    id: "StartDate",
    default: true,
    sortBy: function(event) {
      const getDateWithoutTime = date => {
        const newDate = new Date(date);
        newDate.setHours(0, 0, 0, 0); // set time too midnight to only sort after date
        return newDate;
      };

      if (event.moment?.from) return getDateWithoutTime(event.moment.from);

      if (event.start) return getDateWithoutTime(event.start);

      return "";
    }
  },
  {
    title: "Uhrzeit",
    id: "StartTime",
    default: true,
    sortBy: function(event) {
      const getTimeFromDate = date => {
        const newDate = new Date(date);
        newDate.setFullYear(1970, 0, 1); // set date to one date to only sort after time
        return newDate;
      };

      if (event.moment?.from) return getTimeFromDate(event.moment.from);

      if (event.start) return getTimeFromDate(event.start);

      return "";
    }
  },
  {
    title: "Standort (Übergeordnet)",
    id: "Standort_Übergeordnet",
    default: false,
    sortBy: function(event) {
      return getLocation(event)?.address?.normalized_city;
    }
  },
  {
    title: "Standort (Ort)",
    id: "Standort_Ort",
    default: false,
    sortBy: function(event) {
      return getLocation(event)?.address?.city;
    }
  },
  {
    title: "Titel",
    id: "Titel",
    default: false,
    sortBy: function(event) {
      if (event.moment?.title) return event.moment.title;
      return event.title;
    }
  },
  {
    title: "Veranstalter",
    id: "Veranstalter",
    default: false,
    sortBy: function(event) {
      return getOrganizer(event);
    }
  },
  {
    title: "Kategorie",
    id: "Kategorie",
    default: false,
    sortBy: function(event) {
      return getCategory(event);
    }
  },
  {
    title: "Kategoriegruppe",
    id: "Kategoriegruppe",
    default: false,
    sortBy: function(event) {
      return getCategoryGroup(event);
    }
  }
];

const catHelper = event => {
  const categories =
    flatten(
      store.state.categoryGroupsForEvents?.map(cg => [...cg.categories, cg])
    ) ?? [];
  return (
    categories?.find(category => event.category === category._id)?.title ??
    "---"
  );
};

const catGroupHelper = event => {
  return (
    store.state.categoryGroupsForEvents?.find(
      cg => cg._id === event.category_group
    )?.title ?? "---"
  );
};
export function getNewListHTML(apiKey) {
  const innerHTML = `<div class="eoevents-widget" id="eoevents-list-widget" 
     data-target-uri="https://meineseite/events/xxxSLUGxxx/xxxMOMENTxxx"
     data-list-uri="https://meineseite/events/" 
     data-api-url="${env.events_api_client_url}" 
     data-api-key="${apiKey.apiKey}" 
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-warning="${apiKey.color["color-warning"]}">
</div>
 <div class="eoevents-async-scripts" data-path="${env.events_widget_api_url_v2}">
    <div data-name="eoevents-list"></div>
    </div>
<script type="module" src="${env.events_widget_api_url_v2}loadAsyncEvents.min.js"></script>
<link href="${env.events_widget_api_url_v2}eoevents-Variables.css" rel="stylesheet" type="text/css">
<link href="${env.events_widget_api_url_v2}eoevents-main.css" rel="stylesheet" type="text/css">`;
  return innerHTML;
}

export function getNewDetailHTML(apiKey) {
  const innerHTML = `<div class="eoevents-widget" id="eoevents-dv-widget" 
     data-target-uri="https://meineseite/events/xxxSLUGxxx/xxxMOMENTxxx"
     data-list-uri="https://meineseite/events/" 
     data-api-url="${env.events_api_client_url}" 
     data-api-key="${apiKey.apiKey}" 
     data-event="mein-event"
     data-moment="1234567890abcdefghijkl"
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-warning="${apiKey.color["color-warning"]}">
</div>
 <div class="eoevents-async-scripts" data-path="${env.events_widget_api_url_v2}">
    <div data-name="eoevents-detailView"></div>
    </div>
<script type="module" src="${env.events_widget_api_url_v2}loadAsyncEvents.min.js"></script>
<link href="${env.events_widget_api_url_v2}eoevents-Variables.css" rel="stylesheet" type="text/css">
<link href="${env.events_widget_api_url_v2}eoevents-main.css" rel="stylesheet" type="text/css">`;
  return innerHTML;
}

export function getNewAccordionHTML(apiKey) {
  const innerHTML = `<div class="eoevents-widget" id="eoevents-accordion-widget" 
     data-target-uri="https://meineseite/events/xxxSLUGxxx/xxxMOMENTxxx"
     data-api-url="${env.events_api_client_url}" 
     data-api-key="${apiKey.apiKey}" 
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-warning="${apiKey.color["color-warning"]}">
</div>
 <div class="eoevents-async-scripts" data-path="${env.events_widget_api_url_v2}">
    <div data-name="eoevents-accordion"></div>
    </div>
<script type="module" src="${env.events_widget_api_url_v2}loadAsyncEvents.min.js"></script>
<link href="${env.events_widget_api_url_v2}eoevents-Variables.css" rel="stylesheet" type="text/css">
<link href="${env.events_widget_api_url_v2}eoevents-main.css" rel="stylesheet" type="text/css">`;
  return innerHTML;
}

export function getNewFakeTableHTML(apiKey) {
  const innerHTML = `<div class="eoevents-widget" id="eoevents-table-widget" 
     data-target-uri="https://meineseite/events/xxxSLUGxxx/xxxMOMENTxxx"
     data-api-url="${env.events_api_client_url}" 
     data-api-key="${apiKey.apiKey}" 
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-warning="${apiKey.color["color-warning"]}">
</div>
 <div class="eoevents-async-scripts" data-path="${env.events_widget_api_url_v2}">
    <div data-name="eoevents-faketable"></div>
    </div>
<script type="module" src="${env.events_widget_api_url_v2}loadAsyncEvents.min.js"></script>
<link href="${env.events_widget_api_url_v2}eoevents-Variables.css" rel="stylesheet" type="text/css">
<link href="${env.events_widget_api_url_v2}eoevents-main.css" rel="stylesheet" type="text/css">`;
  return innerHTML;
}

export function getNewTeaserHTML(apiKey) {
  const innerHTML = `<div class="eoevents-widget" id="eoevents-teaser-widget" 
     data-target-uri="https://meineseite/events/xxxSLUGxxx/xxxMOMENTxxx"
     data-api-url="${env.events_api_client_url}" 
     data-api-key="${apiKey.apiKey}" 
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-warning="${apiKey.color["color-warning"]}">
</div>
 <div class="eoevents-async-scripts" data-path="${env.events_widget_api_url_v2}">
    <div data-name="eoevents-teaser"></div>
    </div>
<script type="module" src="${env.events_widget_api_url_v2}loadAsyncEvents.min.js"></script>
<link href="${env.events_widget_api_url_v2}eoevents-Variables.css" rel="stylesheet" type="text/css">
<link href="${env.events_widget_api_url_v2}eoevents-main.css" rel="stylesheet" type="text/css">`;
  return innerHTML;
}
export function getListHTML(apiKey) {
  const innerHTML = `<div class="eoev-app-widget" id="eoev-list"
     data-target-uri="https://meineseite/events/xxxSLUGxxx/xxxMOMENTxxx"
     data-list-uri="https://meineseite/events/"
     data-api-url="${env.events_api_client_url}"
     data-api-key="${apiKey.apiKey}"
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-secondary="${apiKey.color["color-secondary"]}"
     data-color-accent="${apiKey.color["color-accent"]}"
     data-color-error="${apiKey.color["color-error"]}"
     data-color-info="${apiKey.color["color-info"]}"
     data-color-warning="${apiKey.color["color-warning"]}"
     data-map-pin-fill-color="${apiKey.color["map-pin-fill-color"]}"
     data-map-pin-stroke-color="${apiKey.color["map-pin-stroke-color"]}"
     data-map-pin-fill-color-active="${apiKey.color["map-pin-fill-color-active"]}"
     data-map-pin-stroke-color-active="${apiKey.color["map-pin-stroke-color-active"]}"
>
    <svg xmlns="http://www.w3.org/2000/svg" width="150" viewBox="0 0 100 100">
        <circle fill="#00636C" cx="6" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 15 ; 0 -15; 0 15" repeatCount="indefinite" begin=".1"/>
        </circle>
        <circle fill="#00636C" cx="30" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 10 ; 0 -10; 0 10" repeatCount="indefinite" begin=".2"/>
        </circle>
        <circle fill="#00636C" cx="54" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 5 ; 0 -5; 0 5" repeatCount="indefinite" begin=".3"/>
        </circle>
    </svg>
</div>

<div class="eo-async-scripts" data-path="${env.events_widget_api_url}">
    <div data-name="eoev-lib-eoev"></div>
    <div data-name="eoev-lib-vuetify"></div>
    <div data-name="eoev-list"></div>
</div>
<script type="module" src="${env.events_widget_api_url}loadasync.min.js"></script>`;

  return innerHTML;
}

export function getTeaserHTML(apiKey) {
  const innerHTML = `<div class="eoev-teaser-widget" id="eoev-teaser"
     data-target-uri="https://meineseite/events/xxxSLUGxxx/xxxMOMENTxxx"
     data-list-uri="https://meineseite/events/"
     data-api-url="${env.events_api_client_url}"
     data-api-key="${apiKey.apiKey}"
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-secondary="${apiKey.color["color-secondary"]}"
     data-color-accent="${apiKey.color["color-accent"]}"
     data-color-error="${apiKey.color["color-error"]}"
     data-color-info="${apiKey.color["color-info"]}"
     data-color-warning="${apiKey.color["color-warning"]}"
     data-map-pin-fill-color="${apiKey.color["map-pin-fill-color"]}"
     data-map-pin-stroke-color="${apiKey.color["map-pin-stroke-color"]}"
     data-map-pin-fill-color-active="${apiKey.color["map-pin-fill-color-active"]}"
     data-map-pin-stroke-color-active="${apiKey.color["map-pin-stroke-color-active"]}"
>
    <svg xmlns="http://www.w3.org/2000/svg" width="150" viewBox="0 0 100 100">
        <circle fill="#00636C" cx="6" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 15 ; 0 -15; 0 15" repeatCount="indefinite" begin=".1"/>
        </circle>
        <circle fill="#00636C" cx="30" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 10 ; 0 -10; 0 10" repeatCount="indefinite" begin=".2"/>
        </circle>
        <circle fill="#00636C" cx="54" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 5 ; 0 -5; 0 5" repeatCount="indefinite" begin=".3"/>
        </circle>
    </svg>
</div>

<div class="eo-async-scripts" data-path="${env.events_widget_api_url}">
    <div data-name="eoev-lib-eoev"></div>
    <div data-name="eoev-lib-vuetify"></div>
    <div data-name="eoev-teaser"></div>
</div>
<script type="module" src="${env.events_widget_api_url}loadasync.min.js"></script>`;

  return innerHTML;
}

export function getDetailHTML(apiKey) {
  const innerHTML = `<div class="eoev-dv-widget" id="eoev-detail-view"
     data-target-uri="https://meineseite/events/xxxSLUGxxx/xxxMOMENTxxx"
     data-list-uri="https://meineseite/events/"
     data-api-url="${env.events_api_client_url}"
     data-api-key="${apiKey.apiKey}"
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-secondary="${apiKey.color["color-secondary"]}"
     data-color-accent="${apiKey.color["color-accent"]}"
     data-color-error="${apiKey.color["color-error"]}"
     data-color-info="${apiKey.color["color-info"]}"
     data-color-warning="${apiKey.color["color-warning"]}"
     data-map-pin-fill-color="${apiKey.color["map-pin-fill-color"]}"
     data-map-pin-stroke-color="${apiKey.color["map-pin-stroke-color"]}"
     data-map-pin-fill-color-active="${apiKey.color["map-pin-fill-color-active"]}"
     data-map-pin-stroke-color-active="${apiKey.color["map-pin-stroke-color-active"]}"
>
    <svg xmlns="http://www.w3.org/2000/svg" width="150" viewBox="0 0 100 100">
        <circle fill="#00636C" cx="6" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 15 ; 0 -15; 0 15" repeatCount="indefinite" begin=".1"/>
        </circle>
        <circle fill="#00636C" cx="30" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 10 ; 0 -10; 0 10" repeatCount="indefinite" begin=".2"/>
        </circle>
        <circle fill="#00636C" cx="54" cy="50" r="6">
            <animateTransform attributeName="transform" dur="1s" type="translate" values="0 5 ; 0 -5; 0 5" repeatCount="indefinite" begin=".3"/>
        </circle>
    </svg>
</div>

<div class="eo-async-scripts" data-path="${env.events_widget_api_url}">
    <div data-name="eoev-lib-eoev"></div>
    <div data-name="eoev-lib-vuetify"></div>
    <div data-name="eoev-detail-view"></div>
</div>

<script type="module" src="${env.events_widget_api_url}loadasync.min.js"></script>`;

  return innerHTML;
}

export function getReportWidgetHTML(apiKey) {
  const innerHTML = `<div id="eoevents-report-widget" class="eoevents-report-widget"
     data-api-key="${apiKey.apiKey}"
     data-form-name="${apiKey.dataFormName}"
     data-color-text="${apiKey.color["color-text"]}"
     data-color-primary="${apiKey.color["color-primary"]}"
     data-color-secondary="${apiKey.color["color-secondary"]}"
     data-color-accent="${apiKey.color["color-accent"]}"
     data-color-error="${apiKey.color["color-error"]}"
     data-color-info="${apiKey.color["color-info"]}"
     data-color-warning="${apiKey.color["color-warning"]}"
     ></div>

<div class="eoevents-report-async-scripts" data-path="${env.events_report_widget_api_url}">
    <div data-name="eoevents-report-reportWidget"></div>
</div>
<script type="module" src="${env.events_report_widget_api_url}loadAsyncEventReport.min.js"></script>
<link href="${env.events_report_widget_api_url}eoevents-report-Variables.css" rel="stylesheet" type="text/css">`;
  return innerHTML;
}

export const imports = [
  {
    typeId: "Feratel-ID",
    isActive: principal => {
      const login = principal.additional_data?.feratelLogins;
      return login?.some(_ => _.importEvents);
    }
  },
  {
    typeId: "Tramino-ID-Tourismus",
    isActive: principal => {
      const login = principal.additional_data?.traminoLogin?.events;
      return login?.apiUrl?.length;
    }
  },
  {
    typeId: "DestinationOne-ID",
    isActive: principal => {
      const login = principal.additional_data?.destinationOneLogin?.events;
      return login?.apiUrl?.length && login?.types?.length;
    }
  },
  {
    typeId: "Eventim-ID",
    isActive: principal => {
      const login = principal.additional_data?.eventimLogin;
      return login?.active;
    }
  }
];
